// The selected device, in full — the third column of the User Access page.
//
// The transfer lists can only show a name and a unique number, because that is
// all the mapping endpoints return. The question an admin actually has when
// looking at someone's access is usually about the DEVICE — whose is it, where
// is it installed, is it alive, what firmware is on it — so the row is fetched
// in full and shown beside the lists rather than in a dialog that would hide
// the lists it belongs to.
//
// ⚠️ EVERY TIMESTAMP HERE IS IST LABELLED AS UTC. The server writes local time
// with a Z on it; parseServerTime applies the same correction deviceStatus uses
// for heartbeats. Printing the raw value shows a device last seen five and a
// half hours in the future.

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Divider, CircularProgress, Chip, Button,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { getDeviceDetail, getUsersForDevice } from '../../../../core/api/api';
import { parseServerTime, firmwareStateOf, CURRENT, STALE } from '../../../../core/devices/firmwareInfo';
import { liveDotOf, heartbeatOf } from '../../../../core/devices/firmwareFleet';
import { deviceLabel } from '../../../../core/devices/deviceMeta';

const when = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));

function Row({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.75, py: 0.125, alignItems: 'baseline' }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: 10.5, width: 62, flexShrink: 0, textAlign: 'right' }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          fontFamily: mono ? 'monospace' : undefined,
          wordBreak: 'break-word',
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function DeviceDetailsPanel({
  device, user, onUnassign, onUnassignUser,
}) {
  const uno = device ? String(device.UniqueNumber ?? '') : '';
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  // Everyone this device is mapped to. Fetched separately because it needs the
  // INSTALL id, which only arrives with the row above.
  const [access, setAccess] = useState([]);

  useEffect(() => {
    if (!uno) { setRow(null); return undefined; }
    let alive = true;
    setLoading(true);
    // Cleared before the fetch, not after: leaving the previous device's row on
    // screen while the next one loads reads as "these are its details", and on
    // an admin page that is how the wrong device gets changed.
    setRow(null);
    setAccess([]);
    getDeviceDetail(uno)
      .then((res) => {
        if (!alive) return;
        const r = (Array.isArray(res.data) ? res.data[0] : null) || null;
        setRow(r);
        if (r && r.ID != null) {
          getUsersForDevice(r.ID)
            .then((u) => { if (alive) setAccess(Array.isArray(u.data) ? u.data : []); })
            .catch(() => { if (alive) setAccess([]); });
        }
      })
      .catch(() => { if (alive) setRow(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [uno]);

  const shell = (children) => (
    <Paper
      variant="outlined"
      sx={{
        gridArea: 'details',
        p: 0.625, minHeight: 0, display: 'flex', flexDirection: 'column',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 12.5, mb: 0.375, flexShrink: 0 }}>
        Device Details
      </Typography>
      <Divider sx={{ mb: 0.5 }} />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</Box>
    </Paper>
  );

  if (!uno) {
    return shell(
      <Typography color="text.secondary" sx={{ fontSize: 11 }}>
        Pick a device to see where it is installed, who owns it and what
        firmware it is on.
      </Typography>,
    );
  }

  if (loading) {
    return shell(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: 11 }}>Loading…</Typography>
      </Box>,
    );
  }

  if (!row) {
    return shell(
      <Typography color="error" sx={{ fontSize: 11 }}>
        {`Could not load ${uno}. It is mapped to this user but has no
        DevicesInstalled row — which is why it may be showing nowhere else.`}
      </Typography>,
    );
  }

  const fw = firmwareStateOf(row);
  /*
   * Does the SELECTED user actually hold a mapping to this device?
   *
   * ⚠️ Read from `access`, not assumed from which list the row was clicked in.
   * A device reached from the AVAILABLE pane is by definition not this user's —
   * and offering "Remove from AMIT" there would be a button that deletes
   * nothing and reports success.
   */
  const hasThisUser = !!(user && access.some(
    (u) => String(u.UserId) === String(user.UserId),
  ));
  /*
   * ⚠️ LIVENESS COMES FROM THE LIST ROW, NOT FROM THE ROW FETCHED HERE, and the
   * two are not interchangeable. `GetDeviceDetailbyUniqueNumber` carries 106
   * columns and NEITHER of the ones liveness is judged by — no `Connected`, no
   * `NetworkLastUpdatedDateTime` (probed on 802837). The list row has both,
   * merged in from the location list by loadRoomDevices.
   *
   * That is also what keeps this chip and the dot beside it saying the same
   * thing: one row, one rule (`liveDotOf`). They disagreed before — thirteen
   * red dots against an "Online" chip — because each computed its own answer
   * from a different column with a different clock correction.
   *
   * `device ?? row` rather than `device` alone: the fetched row still answers
   * through LastMqttPing if this is ever opened without a list row behind it.
   */
  const online = liveDotOf(device || row);
  const seen = parseServerTime(heartbeatOf(device || row));

  return shell(
    <>
      {/* THE NAME LEADS. This panel used to open on the unique number alone,
          on the reasoning that the name was already the highlighted row to the
          left — but the list truncates ("2 TOUCH SWIT…"), the highlight is one
          of four panels competing for the eye, and every destructive button in
          this column acts on THIS device. The name is what an operator checks
          before pressing them, so it is stated here rather than inferred from
          a selection two panels away. Location and room stay out: they are the
          panel TITLES above, which do not truncate.

          ⚠️ The name is taken from the fetched row first — `device` is the list
          row, and the two unfiltered replies spell the customer's name and the
          product name into the same column the other way round (see
          core/devices/favouriteNames). */}
      <Typography
        sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.25, mb: 0.25 }}
        title={row.DeviceName || device?.DeviceName || ''}
      >
        {row.DeviceName || device?.DeviceName || '(unnamed)'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
          {uno}
        </Typography>
        {/* Three-valued, like the dot: null means no heartbeat column answered,
            and "we have not been told" must not be drawn as "Offline". */}
        <Chip
          size="small"
          label={online == null ? 'Not known' : (online ? 'Online' : 'Offline')}
          color={online ? 'success' : 'default'}
          variant={online ? 'filled' : 'outlined'}
          sx={{ height: 16, fontSize: 9.5 }}
        />
      </Box>

      <Row label="Type" value={`${deviceLabel(row.DeviceID)} · ID ${row.DeviceID}`} />
      <Row label="Product" value={row.DeviceTypeName} />
      <Row label="Channels" value={row.Outputs} />
      <Row label="Owner" value={row.DeviceMasterUserName} />
      <Row label="Install id" value={row.ID} />
      <Divider sx={{ my: 0.5 }} />

      <Row label="Last seen" value={when(seen)} />
      {/* ⚠️ A DIFFERENT CLOCK FROM "Last seen". This moves on ANY write to the
          install row — an assignment, a rename, a firmware mark — including
          ours. A device silent for months can carry a recent date here, so the
          two are never interchangeable. 801824 reads 19 Feb 2026 while its
          LastMqttPing is still 1901. */}
      <Row label="Row changed" value={when(parseServerTime(row.LastUpdatedDateTime))} />
      <Row label="Status" value={row.Status} mono />
      <Divider sx={{ my: 0.5 }} />

      {/* The same three facts Update Code decides on, so the two screens can
          never disagree about whether a device is current. */}
      <Row label="Firmware" value={fw.binFile || '—'} mono />
      <Row label="Built" value={when(fw.builtAt)} />
      <Row label="Flashed" value={when(fw.flashedAt)} />
      <Box sx={{ mt: 0.5 }}>
        <Chip
          size="small"
          variant="outlined"
          color={fw.state === CURRENT ? 'success' : (fw.state === STALE ? 'warning' : 'default')}
          label={fw.state === CURRENT ? 'Up to date'
            : (fw.state === STALE ? 'Needs update' : 'Firmware not known')}
          sx={{ height: 17, fontSize: 9.5 }}
        />
      </Box>

      <Divider sx={{ my: 0.5 }} />

      {/* ── Who can actually see it ────────────────────────────────────────
          ⚠️ OWNERSHIP AND ACCESS ARE TWO DIFFERENT FACTS, and the panel used to
          show only the first. `MasterUserId` says whose device it is;
          UserDeviceMapping says who can open it. A device can have an owner and
          be mapped to NOBODY — 801824 is exactly that — in which case it
          appears in no one's app while still reading as assigned. */}
      <Typography color="text.secondary" sx={{ fontSize: 10.5, mb: 0.25 }}>
        {`Assigned to (${access.length})`}
      </Typography>
      {access.length === 0 ? (
        <Typography sx={{ fontSize: 11, color: 'warning.main' }}>
          Nobody — mapped to no user, so it shows in no one&apos;s app.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {access.map((u) => (
            <Chip
              key={u.UserId}
              size="small"
              variant={String(u.IsMasterUser) === '1' ? 'filled' : 'outlined'}
              color={String(u.IsActive) === '0' ? 'default' : 'primary'}
              label={`${u.Name || `User ${u.UserId}`} · ${u.UserId}`}
              sx={{ height: 17, fontSize: 9.5 }}
            />
          ))}
        </Box>
      )}

      {/* ── Detach the device from its installation ────────────────────────
          A DIFFERENT ACT FROM THE ← ARROW, and the difference is the whole
          reason it lives here rather than on a list.

          ⚠️ The arrow removes one USER's access. This clears the device's
          OWNER, LOCATION and ROOM — it stops belonging to anybody, which is
          what "wrongly assigned" or "replaced" actually calls for. Doing only
          the arrow leaves a device that is installed at a site nobody can see.

          Offered on both lists, because a device that is wrongly assigned is
          usually one this user does NOT have — which is exactly why it shows
          up in Available. */}
      {row && (onUnassign || onUnassignUser) && (
        <>
          <Divider sx={{ my: 0.75 }} />

          {/* ⚠️ TWO DIFFERENT ACTS, SIDE BY SIDE AND NAMED APART. Collapsing
              them into one "unassign" is how a support call meant to remove one
              person's access ends up detaching a working device from its site.
              The narrow one is offered FIRST, because it is the one that is
              usually meant and the only one that is easily undone. */}
          {onUnassignUser && hasThisUser && (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<PersonRemoveIcon fontSize="small" />}
              onClick={() => onUnassignUser(row)}
              sx={{ textTransform: 'none', py: 0, mb: 0.5, display: 'block' }}
            >
              {`Remove from ${user ? (user.Name || `user ${user.UserId}`) : 'this user'}`}
            </Button>
          )}

          {/* Said plainly when the narrow button is missing, so its absence
              never reads as the feature being broken. */}
          {onUnassignUser && !hasThisUser && user && (
            <Typography color="text.secondary" sx={{ fontSize: 9.5, display: 'block', mb: 0.5 }}>
              {`${user.Name || `User ${user.UserId}`} is not mapped to this device, so there is nothing to remove.`}
            </Typography>
          )}

          {onUnassign && (
            <>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<LinkOffIcon fontSize="small" />}
                onClick={() => onUnassign(row)}
                sx={{ textTransform: 'none', py: 0, display: 'block' }}
              >
                Unassign from everyone
              </Button>
              <Typography color="text.secondary" sx={{ fontSize: 9.5, display: 'block', mt: 0.25 }}>
                Clears its owner, location and room — the device leaves every
                user&apos;s app, not just this one&apos;s.
              </Typography>
            </>
          )}
        </>
      )}
    </>,
  );
}
